import { useEffect, useMemo } from 'react';

import { ENotificationsFilter } from 'modules/notifications/const';
import { INotificationProps } from 'modules/notifications/types';
import { filterNotificationByCategory } from 'modules/notifications/utils/filterNotificationByCategory';
import { mapNotificationsToRowData } from 'modules/notifications/utils/mapNotificationsToRowData';
import { useNotifications } from 'modules/notifications/hooks/useNotifications';
import { useAccount } from 'modules/account/hooks/useAccount';

import { EmptyState } from '../EmptyState';
import { ErrorState } from '../ErrorState';
import { LoadingState } from '../LoadingState';
import { Notifications } from '../Notifications';

interface IContentProps {
  activeFilter: ENotificationsFilter;
  isOpened: boolean;
  onClose: () => void;
}

export const Content = ({ activeFilter, isOpened, onClose }: IContentProps) => {
  const { group } = useAccount();

  const { isError, isLoading, notifications } = useNotifications({
    skipFetching: false,
    limit: 15,
    group,
  });

  const { handleRefetchNotifications } = useNotifications({
    only_unseen: true,
    group,
  });

  useEffect(() => {
    if (isOpened) {
      handleRefetchNotifications();
    }
    // no need to track handleRefetchNotifications
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpened]);

  const preparedNotifications: INotificationProps[] = useMemo(() => {
    return (
      notifications?.notifications
        .filter(notification =>
          filterNotificationByCategory(notification, activeFilter),
        )
        .map(mapNotificationsToRowData) ?? []
    );
  }, [activeFilter, notifications]);

  if (isLoading) return <LoadingState />;

  if (isError) return <ErrorState />;

  if (preparedNotifications.length === 0) return <EmptyState />;

  return (
    <Notifications notifications={preparedNotifications} onClose={onClose} />
  );
};
